import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  Type,
  ViewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { ListEntryTypes, ListProperty, User } from '@site-mate/dashpivot-shared-library';

import { AbstractListHeader } from 'app/lists/abstract-list-header/abstract-list-header.component';
import { ListHeaderAttachmentComponent } from 'app/lists/list-header-attachment/list-header-attachment.component';
import { ListHeaderDateComponent } from 'app/lists/list-header-date/list-header-date.component';
import { ListHeaderDateExpiryComponent } from 'app/lists/list-header-date-expiry/list-header-date-expiry.component';
import { ListPropertyColumnMovedEvent } from 'app/lists/list-header-edit/list-property-column-moved.interface';
import { ListHeaderNumberComponent } from 'app/lists/list-header-number/list-header-number.component';
import { ListHeaderTextComponent } from 'app/lists/list-header-text/list-header-text.component';

import { ListPropertyHostDirective } from '../list-property-host.directive';

@Component({
  selector: 'cc-list-header-property',
  templateUrl: './list-header-property.component.html',
  styleUrls: ['./list-header-property.component.scss'],
})
export class ListHeaderPropertyComponent implements AfterViewInit {
  @Input() index: number;
  @Input() isLast: boolean;
  @Input() property: ListProperty;
  @Input() editable = true;
  @Input() users: User[];
  @Output() readonly propertyChanged = new EventEmitter<ListProperty>();
  @Output() readonly propertyDeleted = new EventEmitter<ListProperty>();
  @Output() readonly onPropertyMoved = new EventEmitter<ListPropertyColumnMovedEvent>();
  @Output() readonly onRequestAddProperty = new EventEmitter<number>();

  componentInstance: AbstractListHeader;

  private readonly componentMap = new Map<string, Type<AbstractListHeader>>([
    [ListEntryTypes.Text, ListHeaderTextComponent],
    [ListEntryTypes.Number, ListHeaderNumberComponent],
    [ListEntryTypes.Date, ListHeaderDateComponent],
    [ListEntryTypes.DateExpiry, ListHeaderDateExpiryComponent],
    [ListEntryTypes.Attachment, ListHeaderAttachmentComponent],
  ]);

  @ViewChild(ListPropertyHostDirective, { static: true }) dynamicHost: ListPropertyHostDirective;
  unSubscribe = new Subject<void>();
  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.loadComponent();
    this.cdr.detectChanges();
  }

  ngOnChanges(): void {
    if (this.componentInstance) {
      this.assignComponentProperties(this.componentInstance);
    }
  }

  loadComponent() {
    const componentType = this.componentMap.get(this.property.kind);

    const component = this.dynamicHost.viewContainerRef.createComponent(componentType);
    this.assignComponentProperties(component.instance);
    this.handleComponentEvents(component.instance);

    this.componentInstance = component.instance;
  }

  private assignComponentProperties(componentInstance: AbstractListHeader) {
    componentInstance.index = this.index;
    componentInstance.isLast = this.isLast;
    componentInstance.property = this.property;
    componentInstance.editable = this.editable;
    componentInstance.users = this.users;
  }

  private handleComponentEvents(componentInstance: AbstractListHeader) {
    componentInstance.propertyChanged
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((property) => this.propertyChanged.emit(property));
    componentInstance.propertyDeleted
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((property) => this.propertyDeleted.emit(property));
    componentInstance.onPropertyMoved
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((event) => this.onPropertyMoved.emit(event));
    componentInstance.onRequestAddProperty
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((index) => this.onRequestAddProperty.emit(index));
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
